import React, { useState, useEffect, useCallback } from 'react';
import FileDropZone from '../shared/FileDropZone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes, faUpload, faFileText, faRobot, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../utils/axiosConfig';
import { shouldDiscardOnClose } from './captureDiscardRule';
import { SUPPLIER_DOCUMENT_ACCEPT, supplierDocumentProblem } from './supplierDocumentFile';
import './InvoiceCaptureModal.css';
import { crmService } from '../../services/crmService';
import systemDataService from '../../services/systemDataService';
import { stockService } from '../../services/stockService';
import InvoiceExtractionResults from './InvoiceExtractionResults';
import InvoiceCrmQuickCreate from './InvoiceCrmQuickCreate';
import { getErrorMessage } from '../../utils/errorMessages';
import { canMergeLineItems, mergeLineItems } from './utils/lineItemMerge';
import { PRICE_DECIMALS, formatFigure } from '../../utils/precision';
import { isServiceResult } from './utils/pickedSearchResult';


const InvoiceCaptureModal = ({ isOpen, onClose, onSuccess }) => {
    const [file, setFile] = useState(null);
    const [uploadState, setUploadState] = useState('idle');
    const [error, setError] = useState('');
    const [extractedData, setExtractedData] = useState(null);
    const [selectedFields, setSelectedFields] = useState({});
    const [selectedLineItems, setSelectedLineItems] = useState([]);
    const [analysisId, setAnalysisId] = useState(null);
    const [reAnalysisGuidance, setReAnalysisGuidance] = useState('');
    const [crmMatch, setCrmMatch] = useState(null);
    const [extractionStep, setExtractionStep] = useState(0);
    const [pageProgress, setPageProgress] = useState({ current: 0, total: 0 });
    const [unrecognizedUnits, setUnrecognizedUnits] = useState([]);
    const [availableUnits, setAvailableUnits] = useState([]);
    const [unitResolutions, setUnitResolutions] = useState({});
    const [hasDefaultedUnits, setHasDefaultedUnits] = useState(false);
    // Sprint A — Capture Mode chooser. Modes:
    //   'use_as_is'           : keep captured line items individually (current behaviour)
    //   'consolidate'         : merge selected lines into a single line (Sprint B will surface this option)
    //   'header_totals_only'  : skip line items, emit one synthetic line carrying header net/vat/gross
    const [captureMode, setCaptureMode] = useState('use_as_is');
    // Sprint C — Apply runs a parallel variant search to auto-link captured
    // lines to stock variants. isApplying disables Apply/Discard while this
    // short async batch is in flight (hard 3s timeout, see annotateWithProductMatches).
    const [isApplying, setIsApplying] = useState(false);
    const [extractionSteps] = useState([
        'Analyzing document format and structure...',
        'Converting document to processable format...',
        'Extracting text using advanced OCR and parsing...',
        'Applying AI analysis to identify invoice details...',
        'Extracting supplier information and amounts...',
        'Identifying line items and tax information...',
        'Validating and structuring extracted data...'
    ]);

    const processUnrecognizedUnits = useCallback((data) => {
        if (!data) return;
        const unrecognized = data._unrecognized_units || [];
        const units = data._available_units || [];
        const defaulted = data._has_defaulted_units || false;
        setUnrecognizedUnits(unrecognized);
        setHasDefaultedUnits(defaulted);
        if (units.length > 0) {
            setAvailableUnits(units);
        } else if (unrecognized.length > 0 || defaulted) {
            systemDataService.getUnits().then(dbUnits => {
                const active = (dbUnits || []).filter(u => u.is_active);
                setAvailableUnits(active);
            }).catch(() => {});
        }
        setUnitResolutions({});
    }, []);

    const handleAddUnitToSystem = useCallback(async (unitCode) => {
        try {
            const sanitizedCode = unitCode.toUpperCase().replace(/[^A-Z0-9_-]/g, '_').substring(0, 20);
            const result = await systemDataService.createUnit({
                code: sanitizedCode,
                name: unitCode,
                description: `${unitCode} (added from invoice capture)`,
                is_active: true,
            });
            const newUnit = result.unit || result;
            setAvailableUnits(prev => [...prev, { id: newUnit.id, code: newUnit.code, name: newUnit.name }]);
            setUnitResolutions(prev => ({ ...prev, [unitCode]: { action: 'added', code: newUnit.code } }));
            setUnrecognizedUnits(prev => prev.filter(u => u !== unitCode));

            if (extractedData?.line_items) {
                const updated = { ...extractedData };
                updated.line_items = updated.line_items.map(item => {
                    if (item.unit === unitCode || (item._unrecognized_unit && item.unit === unitCode)) {
                        return { ...item, unit: newUnit.code, _unrecognized_unit: false };
                    }
                    return item;
                });
                setExtractedData(updated);
            }
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Failed to add unit to system';
            setError(`Could not add unit "${unitCode}": ${msg}`);
            throw err;
        }
    }, [extractedData]);

    const handleSelectExistingUnit = useCallback((originalUnit, selectedCode) => {
        setUnitResolutions(prev => ({ ...prev, [originalUnit]: { action: 'mapped', code: selectedCode } }));
        setUnrecognizedUnits(prev => prev.filter(u => u !== originalUnit));

        if (extractedData?.line_items) {
            const updated = { ...extractedData };
            updated.line_items = updated.line_items.map(item => {
                if (item.unit === originalUnit && item._unrecognized_unit) {
                    return { ...item, unit: selectedCode, _unrecognized_unit: false };
                }
                return item;
            });
            setExtractedData(updated);
        }
    }, [extractedData]);

    const handleChangeDefaultedUnit = useCallback((lineIndex, selectedCode) => {
        if (!extractedData?.line_items) return;
        const updated = { ...extractedData };
        updated.line_items = updated.line_items.map((item, idx) => {
            if (idx === lineIndex) {
                return { ...item, unit: selectedCode, _defaulted_unit: false };
            }
            return item;
        });
        const stillHasDefaulted = updated.line_items.some(item => item._defaulted_unit);
        setHasDefaultedUnits(stillHasDefaulted);
        setExtractedData(updated);
    }, [extractedData]);

    const pollProgress = useCallback(async (id) => {
        try {
            const response = await axiosInstance.get(`/api/invoicing/incoming-invoices/capture_progress/${id}/`);
            const { current_page, total_pages, is_complete } = response.data;
            setPageProgress({ current: current_page || 0, total: total_pages || 0 });
            return is_complete;
        } catch (err) {
            console.error('Progress polling error:', err);
            return false;
        }
    }, []);

    useEffect(() => {
        let progressInterval;
        if (uploadState === 'extracting' && analysisId) {
            progressInterval = setInterval(async () => {
                const isComplete = await pollProgress(analysisId);
                if (isComplete) clearInterval(progressInterval);
            }, 500);
        }
        return () => { if (progressInterval) clearInterval(progressInterval); };
    }, [uploadState, analysisId, pollProgress]);

    // Keyed on the supplier's NAME, not on the whole extraction: fixing a unit
    // replaces extractedData, and re-running the lookup then threw away a
    // partner just added from this capture (or swapped it for the first hit).
    const supplierName = extractedData?.supplier_name;
    useEffect(() => {
        if (supplierName) {
            setCrmMatch(null);
            const checkCrm = async () => {
                try {
                    const results = await crmService.searchProfilesByName(supplierName);
                    const approved = results.filter(p => (p.approval_status || '').toString().toLowerCase() === 'approved' || p.approval_status === undefined);
                    if (approved.length > 0) {
                        const match = approved[0];
                        setCrmMatch({
                            matched: true,
                            partner: {
                                id: match.id,
                                legal_entity_name: match.legal_entity_name,
                                trading_short_name: match.trading_short_name,
                            }
                        });
                    } else {
                        setCrmMatch({ matched: false });
                    }
                } catch (err) {
                    console.error('CRM lookup failed:', err);
                    // A refused or failed lookup is not "not found": saying so
                    // offered Add Partner, which made duplicate partners.
                    setCrmMatch({
                        matched: false,
                        lookupFailed: getErrorMessage(err, 'The CRM could not be checked.'),
                    });
                }
            };
            checkCrm();
        }
    }, [supplierName]);

    useEffect(() => {
        let interval;
        if (uploadState === 'extracting') {
            setExtractionStep(0);
            interval = setInterval(() => {
                setExtractionStep(prev => (prev < extractionSteps.length - 1) ? prev + 1 : 0);
            }, 3000);
        }
        return () => { if (interval) clearInterval(interval); };
    }, [uploadState, extractionSteps.length]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        setFile(selectedFile);
        setError('');
    };

    /* The drop zone itself is components/shared/FileDropZone. This is only
       what to do with the file once it arrives, so the file picker and the
       drop cannot end up disagreeing about it. */
    const acceptDroppedFile = (droppedFile) => {
        setFile(droppedFile);
        setError('');
    };

    // The file rule is shared with Attach document; see supplierDocumentFile.js.
    const validateFile = (f) => supplierDocumentProblem(f);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const fileError = validateFile(file);
        if (fileError) { setError(fileError); return; }

        setUploadState('extracting');
        setError('');

        try {
            const formData = new FormData();
            formData.append('file', file);
            if (reAnalysisGuidance) formData.append('guidance', reAnalysisGuidance);

            const response = await axiosInstance.post('/api/invoicing/incoming-invoices/upload_and_capture/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (response.data.status === 'processing' && response.data.analysis_id) {
                setAnalysisId(response.data.analysis_id);
                const currentAnalysisId = response.data.analysis_id;
                let pollAttempts = 0;
                const maxPollAttempts = 300;

                const pollForCompletion = async () => {
                    try {
                        const progressResponse = await axiosInstance.get(
                            `/api/invoicing/incoming-invoices/capture_progress/${currentAnalysisId}/`
                        );
                        const { is_complete, status, extracted_data, error_message } = progressResponse.data;

                        if (is_complete) {
                            if (status === 'failed') {
                                throw new Error(error_message || 'Analysis failed');
                            }

                            const validData = extracted_data || {};
                            setExtractedData(validData);
                            processUnrecognizedUnits(validData);
                            setUploadState('results');

                            const initialSelected = {};
                            Object.keys(validData).forEach(key => {
                                if (key !== 'line_items' && key.charAt(0) !== '_' && validData[key] !== null) {
                                    initialSelected[key] = true;
                                }
                            });
                            if (validData.line_items && validData.line_items.length > 0) {
                                initialSelected['line_items'] = true;
                                setSelectedLineItems(new Array(validData.line_items.length).fill(true));
                            }
                            setSelectedFields(initialSelected);
                            return;
                        }

                        pollAttempts++;
                        if (pollAttempts < maxPollAttempts) {
                            setTimeout(pollForCompletion, 1000);
                        } else {
                            throw new Error('Analysis timeout - processing took too long');
                        }
                    } catch (err) {
                        console.error('Polling error:', err);
                        setError(err.message || 'Failed to check analysis progress');
                        setUploadState('error');
                    }
                };

                setTimeout(pollForCompletion, 1000);
            }
        } catch (err) {
            console.error('Invoice capture error:', err);
            if (err.response?.status === 400) {
                setError(getErrorMessage(err, 'Invoice capture failed. Please check your file.'));
            } else if (err.response?.status === 413) {
                setError('File too large. Please upload a smaller file.');
            } else if (err.response?.status >= 500) {
                setError('Server error during invoice capture. Please try again later.');
            } else {
                setError('Invoice capture failed. Please check your connection and try again.');
            }
            setUploadState('error');
        }
    };

    const discardAnalysis = async (id) => {
        if (!id) return;
        try {
            await axiosInstance.delete(`/api/invoicing/incoming-invoices/discard_capture/${id}/`);
        } catch (err) {
            console.error('Failed to discard analysis:', err);
        }
    };

    // A capture that FAILED is kept on purpose (2026-09-28). The rule and the
    // reason live in captureDiscardRule.js, where they are tested: `'error'` used
    // to be a discard state, so every failed capture deleted the only evidence
    // that it had ever been attempted.
    const handleClose = async () => {
        if (analysisId && shouldDiscardOnClose(uploadState)) {
            await discardAnalysis(analysisId);
        }
        setFile(null);
        setError('');
        setUploadState('idle');
        setExtractedData(null);
        setSelectedFields({});
        setSelectedLineItems([]);
        setAnalysisId(null);
        setReAnalysisGuidance('');
        setCrmMatch(null);
        setPageProgress({ current: 0, total: 0 });
        setUnrecognizedUnits([]);
        setAvailableUnits([]);
        setUnitResolutions({});
        setHasDefaultedUnits(false);
        setCaptureMode('use_as_is');
        setIsApplying(false);
        onClose();
    };

    const handleFieldToggle = (fieldName) => {
        setSelectedFields(prev => ({ ...prev, [fieldName]: !prev[fieldName] }));
    };

    const handleLineItemToggle = (idx) => {
        setSelectedLineItems(prev => {
            const updated = [...prev];
            updated[idx] = !updated[idx];
            return updated;
        });
    };

    const handleToggleAllLineItems = () => {
        const allSelected = selectedLineItems.every(Boolean);
        setSelectedLineItems(prev => prev.map(() => !allSelected));
    };

    const handleDiscard = async () => {
        if (analysisId) {
            await discardAnalysis(analysisId);
        }
        setFile(null);
        setError('');
        setUploadState('idle');
        setExtractedData(null);
        setSelectedFields({});
        setSelectedLineItems([]);
        setAnalysisId(null);
        setReAnalysisGuidance('');
        setCrmMatch(null);
        setPageProgress({ current: 0, total: 0 });
        setUnrecognizedUnits([]);
        setAvailableUnits([]);
        setUnitResolutions({});
        setHasDefaultedUnits(false);
        setCaptureMode('use_as_is');
        setIsApplying(false);
        onClose();
    };

    // Sprint B — replace selected captured lines with one merged line.
    // Pure client-side reshape of extractedData; no backend call.
    const handleMergeSelectedLineItems = () => {
        if (!extractedData?.line_items) return;
        const indices = (selectedLineItems || [])
            .map((sel, idx) => sel ? idx : -1)
            .filter(idx => idx >= 0);
        if (indices.length < 2) return;

        const items = indices.map(i => extractedData.line_items[i]);
        const check = canMergeLineItems(items);
        if (!check.ok) {
            setError(check.reason);
            return;
        }
        setError('');

        const merged = mergeLineItems(items);
        const firstIdx = indices[0];
        const indexSet = new Set(indices);

        const newLineItems = extractedData.line_items
            .map((item, idx) => {
                if (idx === firstIdx) return merged;
                if (indexSet.has(idx)) return null;
                return item;
            })
            .filter(item => item !== null);

        setExtractedData({ ...extractedData, line_items: newLineItems });
        setSelectedLineItems(new Array(newLineItems.length).fill(true));
    };

    // Sprint A — synthesize a single line item carrying the captured header
    // totals. Used when the user picks the "Skip line items, header totals only"
    // capture mode. The form-side guard at IncomingInvoiceScreen2.calculateTotals
    // recognises the _capture_skip_lines flag and preserves the captured header
    // totals on invoiceData until the user starts editing.
    const buildSyntheticHeaderLine = (data) => {
        const net = parseFloat(data.net_amount) || 0;
        const vat = parseFloat(data.vat_amount) || 0;
        const gross = parseFloat(data.gross_amount) || (net + vat);
        return {
            item_description: 'As per supplier invoice (header totals)',
            quantity: 1,
            unit: null,
            // Quantity 1, so the price IS the net -- kept to PRICE_DECIMALS
            // (at least 2) rather than cut to cents.
            unit_price: formatFigure(net, 2, PRICE_DECIMALS),
            net_amount: net.toFixed(2),
            vat_rate_percentage: net > 0 ? ((vat / net) * 100).toFixed(2) : null,
            vat_amount: vat.toFixed(2),
            gross_amount: gross.toFixed(2),
            _capture_skip_lines: true,
        };
    };

    // Strip diagnostic-only fields that the create form doesn't expect.
    // The form contract (CreateIncomingInvoice.js line 305+) only reads the
    // capture-namespaced flags it knows about (_capture_unit_code,
    // _capture_vat_rate_percentage, _capture_skip_lines). The merge bookkeeping
    // fields (_merged_*, _merge_*) and the unit-resolution scratch flags
    // (_unrecognized_unit, _defaulted_unit) are component-local diagnostics.
    const stripDiagnosticFields = (item) => {
        const clean = { ...item };
        delete clean._unrecognized_unit;
        delete clean._defaulted_unit;
        delete clean._merged_from_count;
        delete clean._merge_mixed_unit;
        delete clean._merge_mixed_vat;
        return clean;
    };

    // Sprint C — auto-match a single captured line to a stock variant.
    // Synthetic header-totals lines and merged lines never represent a single
    // product; we skip them rather than mislabel them.
    //
    // Match strategy:
    //   - Call stockService.searchVariantsForInvoice(description, 'INCOMING').
    //     The backend's StockVariantSearchView already filters by purchase_price>0
    //     for INCOMING and ranks by relevance, so we trust the top-N order
    //     (decision Q4, see 06_open_questions.md).
    //   - Exact match (case-insensitive) on shortname / base_item_name /
    //     display_name (with the pricing suffix stripped) ⇒ auto-link.
    //   - Otherwise, top-1 result becomes a "Suggested" chip the user
    //     accepts or dismisses on the create form.
    const matchLineToVariant = async (line) => {
        if (line._capture_skip_lines || line._merged_from_count > 0) {
            return { match: null, suggestion: null };
        }
        const desc = (line.item_description || '').trim();
        if (desc.length < 2) return { match: null, suggestion: null };

        try {
            // Products only: a match becomes the line's product link, which
            // can never hold a service (invoice-line-service-saves-as-product).
            const results = (await stockService.searchVariantsForInvoice(desc, 'INCOMING'))
                ?.filter(r => !isServiceResult(r));
            if (!results || results.length === 0) return { match: null, suggestion: null };

            const descLower = desc.toLowerCase();
            const stripPricing = (s) => (s || '').split(' (Sale:')[0].split(' (Purchase:')[0];
            const exact = results.find(v => {
                const candidates = [
                    v.shortname,
                    v.base_item_name,
                    v.full_description,
                    stripPricing(v.display_name),
                ].filter(Boolean).map(s => s.trim().toLowerCase());
                return candidates.includes(descLower);
            });

            if (exact) {
                return {
                    match: {
                        id: exact.id,
                        display_name: stripPricing(exact.display_name) || exact.full_description || desc,
                    },
                    suggestion: null,
                };
            }

            const top = results[0];
            return {
                match: null,
                suggestion: {
                    id: top.id,
                    display_name: stripPricing(top.display_name) || top.full_description || '',
                },
            };
        } catch (err) {
            console.warn('[capture auto-match] variant search failed for', desc, err);
            return { match: null, suggestion: null };
        }
    };

    // Sprint C — batch auto-match with a hard 3-second timeout. If the batch
    // exceeds the timeout (slow network, many lines), we navigate to the form
    // without auto-match annotations rather than blocking the user.
    const annotateWithProductMatches = async (lines) => {
        if (!lines || lines.length === 0) return lines;

        const TIMEOUT_MS = 3000;
        const batch = Promise.all(lines.map(matchLineToVariant));
        const matches = await Promise.race([
            batch,
            new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), TIMEOUT_MS)),
        ]).catch(err => {
            console.warn('[capture auto-match] batch aborted:', err.message);
            return null;
        });

        if (!matches) return lines;

        return lines.map((line, idx) => {
            const m = matches[idx] || {};
            if (m.match) {
                return {
                    ...line,
                    _capture_product_id: m.match.id,
                    _capture_product_display_name: m.match.display_name,
                };
            }
            if (m.suggestion) {
                return {
                    ...line,
                    _capture_product_suggestion: m.suggestion,
                };
            }
            return line;
        });
    };

    // When the reading fails, the document itself has still arrived: the upload
    // succeeded, the server holds the file, and only the AI could not make sense
    // of it (a poor scan, a handwritten invoice, an unusual layout). The rule is
    // that a supplier invoice carries its paper, not that a machine can read it
    // (Chris, 2026-09-30). So the person can attach it as it is and type the
    // figures in themselves -- the save files it exactly as it would a read one.
    const handleAttachWithoutReading = () => {
        if (!analysisId) return;
        const id = analysisId;
        setFile(null);
        setError('');
        setUploadState('idle');
        setExtractedData(null);
        setSelectedFields({});
        setSelectedLineItems([]);
        setAnalysisId(null);
        setReAnalysisGuidance('');
        setCrmMatch(null);
        onSuccess({ selectedData: {}, analysisId: id, crmPartner: null, attachedOnly: true });
    };

    const handleApply = async () => {
        if (isApplying) return;
        setIsApplying(true);
        try {
            const selectedData = {};
            Object.keys(selectedFields).forEach(key => {
                if (key.charAt(0) === '_') return;
                if (selectedFields[key] && extractedData[key]) {
                    if (key === 'line_items') {
                        if (captureMode === 'header_totals_only') {
                            selectedData[key] = [buildSyntheticHeaderLine(extractedData)];
                        } else if (captureMode === 'consolidate') {
                            // Sprint B — merge all currently-selected lines into one
                            // at Apply time. The user can also have pre-merged via
                            // the explicit "Merge selected" button; that just changes
                            // what 'selected' contains here.
                            const selected = extractedData[key].filter((_, idx) => selectedLineItems[idx]);
                            if (selected.length === 0) {
                                selectedData[key] = [];
                            } else if (selected.length === 1) {
                                selectedData[key] = [stripDiagnosticFields(selected[0])];
                            } else {
                                const check = canMergeLineItems(selected);
                                if (check.ok) {
                                    selectedData[key] = [stripDiagnosticFields(mergeLineItems(selected))];
                                } else {
                                    // Fall back to passing the lines through individually
                                    // rather than silently corrupting amounts. The user
                                    // sees a warning message instead.
                                    setError(check.reason);
                                    selectedData[key] = selected.map(stripDiagnosticFields);
                                }
                            }
                        } else {
                            // 'use_as_is' — current behaviour
                            selectedData[key] = extractedData[key]
                                .filter((_, idx) => selectedLineItems[idx])
                                .map(stripDiagnosticFields);
                        }
                    } else {
                        selectedData[key] = extractedData[key];
                    }
                }
            });

            // 'header_totals_only' is a stronger signal than the per-field checkboxes.
            // Force-include the synthetic line + the captured header totals (net/vat/
            // gross / currency) regardless of what selectedFields had checked, so the
            // form's _capture_skip_lines guard finds matching values on both sides.
            if (captureMode === 'header_totals_only') {
                if (!selectedData.line_items) {
                    selectedData.line_items = [buildSyntheticHeaderLine(extractedData)];
                }
                ['net_amount', 'vat_amount', 'gross_amount', 'currency'].forEach(field => {
                    if (extractedData[field] !== null && extractedData[field] !== undefined) {
                        selectedData[field] = extractedData[field];
                    }
                });
            }

            // Sprint C — auto-match each captured line to a stock variant before
            // hand-off to the create form. Synthetic header-totals and merged
            // lines are skipped inside matchLineToVariant. Hard 3s timeout so a
            // slow search never blocks the user on Apply.
            if (selectedData.line_items && selectedData.line_items.length > 0) {
                selectedData.line_items = await annotateWithProductMatches(selectedData.line_items);
            }

            // The English translation travels with the notes it translates.
            if (selectedData.notes && extractedData.notes_en) {
                selectedData.notes_en = extractedData.notes_en;
            }

            onSuccess({ selectedData, analysisId, crmPartner: crmMatch?.partner || null });
            // The capture now belongs to the form. Left in place, the next
            // Capture Invoice reopened these results, and its Discard, close or
            // Re-analyse deleted the capture the form (or the saved invoice)
            // was holding.
            setFile(null);
            setError('');
            setUploadState('idle');
            setExtractedData(null);
            setSelectedFields({});
            setSelectedLineItems([]);
            setAnalysisId(null);
            setReAnalysisGuidance('');
            setCrmMatch(null);
            setPageProgress({ current: 0, total: 0 });
            setUnrecognizedUnits([]);
            setAvailableUnits([]);
            setUnitResolutions({});
            setHasDefaultedUnits(false);
            setCaptureMode('use_as_is');
        } finally {
            setIsApplying(false);
        }
    };

    const handleAddPartner = () => {
        setUploadState('addPartner');
    };

    const handlePartnerCreated = (partner) => {
        setCrmMatch({ matched: true, partner, addedFromCapture: !partner.already_in_crm });
        setUploadState('results');
    };

    const handleBackFromAddPartner = () => {
        setUploadState('results');
    };

    const handleReAnalyze = async (guidance) => {
        if (!file) return;

        if (analysisId) {
            await discardAnalysis(analysisId);
            setAnalysisId(null);
        }

        setReAnalysisGuidance(guidance);
        setUploadState('extracting');
        setError('');
        // Sprint A: reset capture mode so the user explicitly re-picks
        // after the AI returns a new line-item shape.
        setCaptureMode('use_as_is');

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('guidance', guidance);

            const response = await axiosInstance.post('/api/invoicing/incoming-invoices/upload_and_capture/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (response.data.status === 'processing' && response.data.analysis_id) {
                setAnalysisId(response.data.analysis_id);
                const currentAnalysisId = response.data.analysis_id;
                let pollAttempts = 0;

                const pollForCompletion = async () => {
                    try {
                        const progressResponse = await axiosInstance.get(
                            `/api/invoicing/incoming-invoices/capture_progress/${currentAnalysisId}/`
                        );
                        const { is_complete, status, extracted_data, error_message } = progressResponse.data;

                        if (is_complete) {
                            if (status === 'failed') throw new Error(error_message || 'Re-analysis failed');

                            const validData = extracted_data || {};
                            setExtractedData(validData);
                            processUnrecognizedUnits(validData);
                            setUploadState('results');

                            const initialSelected = {};
                            Object.keys(validData).forEach(key => {
                                if (key !== 'line_items' && key.charAt(0) !== '_' && validData[key] !== null) initialSelected[key] = true;
                            });
                            if (validData.line_items && validData.line_items.length > 0) {
                                initialSelected['line_items'] = true;
                                setSelectedLineItems(new Array(validData.line_items.length).fill(true));
                            }
                            setSelectedFields(initialSelected);
                            return;
                        }

                        pollAttempts++;
                        if (pollAttempts < 300) {
                            setTimeout(pollForCompletion, 1000);
                        } else {
                            throw new Error('Re-analysis timeout');
                        }
                    } catch (err) {
                        setError(err.message || 'Re-analysis failed');
                        setUploadState('error');
                    }
                };
                setTimeout(pollForCompletion, 1000);
            }
        } catch (err) {
            setError('Re-analysis failed. Please try again.');
            setUploadState('error');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content contract-upload-modal">
                <div className="modal-header">
                    <h2>
                        {uploadState === 'results' ? 'Invoice Extraction Results'
                            : uploadState === 'addPartner' ? 'Add New CRM Partner'
                            : 'Capture Supplier Invoice'}
                    </h2>
                    <button
                        className="close-button"
                        onClick={handleClose}
                        disabled={uploadState === 'extracting'}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {error && <div className="error-message">{error}</div>}

                {uploadState === 'error' && analysisId && (
                    <div className="capture-attach-anyway" role="status">
                        <p>
                            The document uploaded fine. Only the automatic reading didn't work.
                            You can attach it to this invoice as it is and fill in the details yourself.
                        </p>
                        <button type="button" className="btn btn-primary" onClick={handleAttachWithoutReading}>
                            <FontAwesomeIcon icon={faUpload} /> Attach it anyway
                        </button>
                    </div>
                )}

                {(uploadState === 'idle' || uploadState === 'error') && (
                    <form onSubmit={handleSubmit} className="upload-form">
                        <FileDropZone
                            className={`file-drop-zone ${file ? 'has-file' : ''}`}
                            activeClassName="active"
                            onFiles={(files) => acceptDroppedFile(files[0])}
                            onError={setError}
                        >
                            {file ? (
                                <div className="file-selected">
                                    <FontAwesomeIcon icon={faFileText} className="file-icon" />
                                    <div className="file-info">
                                        <span className="file-name">{file.name}</span>
                                        <span className="file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                                    </div>
                                    <button type="button" className="remove-file" onClick={() => setFile(null)}>
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </div>
                            ) : (
                                <div className="drop-zone-content">
                                    <FontAwesomeIcon icon={faUpload} className="upload-icon" />
                                    <p className="drop-text">
                                        Drag and drop your supplier invoice here, or{' '}
                                        <label className="file-select-link">
                                            browse files
                                            <input
                                                type="file"
                                                onChange={handleFileChange}
                                                accept={SUPPLIER_DOCUMENT_ACCEPT}
                                                style={{ display: 'none' }}
                                            />
                                        </label>
                                    </p>
                                    <p className="file-requirements">
                                        Supported: PDF, Word, Excel, images (max 50MB)
                                    </p>
                                </div>
                            )}
                        </FileDropZone>

                        <div className="upload-info">
                            <h3>AI-Powered Invoice Capture</h3>
                            <ul>
                                <li>AI will extract supplier name, invoice number, dates, and amounts</li>
                                <li>Line items with quantities, prices, and VAT will be identified</li>
                                <li>You can review and select which fields to use</li>
                                <li>Selected data will pre-fill the invoice creation form</li>
                            </ul>
                        </div>

                        <div className="modal-actions centered">
                            <button type="button" className="btn btn-secondary" onClick={handleClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={!file}>
                                <FontAwesomeIcon icon={faRobot} /> Capture Invoice
                            </button>
                        </div>
                    </form>
                )}

                {uploadState === 'extracting' && (
                    <div className="loading-state">
                        <div className="loading-content">
                            <div className="extraction-progress" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                                <p className="current-step" style={{
                                    fontSize: '1rem', fontWeight: '500', color: '#374151',
                                    margin: '0 0 0.75rem 0', display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', gap: '0.5rem', minHeight: '1.5rem'
                                }}>
                                    <FontAwesomeIcon icon={faRobot} style={{ color: '#3b82f6', fontSize: '1.1rem' }} />
                                    {extractionSteps[extractionStep]}
                                </p>
                                {pageProgress.total > 0 && (
                                    <div style={{ fontSize: '0.9rem', color: '#6b7280', fontWeight: '500', marginTop: '0.5rem' }}>
                                        Extracting data from page {pageProgress.current}/{pageProgress.total}
                                    </div>
                                )}
                            </div>
                            <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                                {file && file.size > 10 * 1024 * 1024 ? (
                                    <div style={{
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem',
                                        padding: '0.75rem', backgroundColor: '#fef3c7', border: '1px solid #f59e0b',
                                        borderRadius: '0.375rem', color: '#92400e'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#f59e0b' }} />
                                            <span style={{ fontWeight: '500' }}>
                                                Large file detected ({(file.size / 1024 / 1024).toFixed(1)}MB)
                                            </span>
                                        </div>
                                        <small style={{ fontSize: '0.8rem' }}>
                                            Processing may take up to 2 minutes
                                        </small>
                                    </div>
                                ) : (
                                    <div style={{
                                        padding: '0.5rem', backgroundColor: '#f0f9ff',
                                        border: '1px solid #3b82f6', borderRadius: '0.375rem', color: '#1e40af'
                                    }}>
                                        <small style={{ fontWeight: '500' }}>Typical processing time: 30-60 seconds</small>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {uploadState === 'results' && extractedData && (
                    <InvoiceExtractionResults
                        extractedData={extractedData}
                        selectedFields={selectedFields}
                        onFieldToggle={handleFieldToggle}
                        selectedLineItems={selectedLineItems}
                        onLineItemToggle={handleLineItemToggle}
                        onToggleAllLineItems={handleToggleAllLineItems}
                        onApply={handleApply}
                        onDiscard={handleDiscard}
                        onReAnalyze={handleReAnalyze}
                        reAnalysisGuidance={reAnalysisGuidance}
                        setReAnalysisGuidance={setReAnalysisGuidance}
                        isReAnalyzing={uploadState === 'extracting'}
                        crmMatch={crmMatch}
                        onAddPartner={handleAddPartner}
                        unrecognizedUnits={unrecognizedUnits}
                        availableUnits={availableUnits}
                        onAddUnitToSystem={handleAddUnitToSystem}
                        onSelectExistingUnit={handleSelectExistingUnit}
                        hasDefaultedUnits={hasDefaultedUnits}
                        onChangeDefaultedUnit={handleChangeDefaultedUnit}
                        captureMode={captureMode}
                        onCaptureModeChange={setCaptureMode}
                        onMergeSelectedLineItems={handleMergeSelectedLineItems}
                        isApplying={isApplying}
                    />
                )}

                {uploadState === 'addPartner' && (
                    <InvoiceCrmQuickCreate
                        supplierName={extractedData?.supplier_name || ''}
                        extracted={extractedData || {}}
                        onPartnerCreated={handlePartnerCreated}
                        onBack={handleBackFromAddPartner}
                    />
                )}
            </div>
        </div>
    );
};

export default InvoiceCaptureModal;
